import type { FilterValue } from "hooks/useFilters";
import { ComplimentaryType, Tier } from "client/api-v2";
import type { FilterSectionFieldProps } from "components/FilterLayout/FilterSection";
import { FilterSectionType } from "components/FilterLayout/FilterSection";

// Header filters - displayed at the top for quick access
export const subscriptionHeaderSections: FilterSectionFieldProps[] = [
  {
    type: FilterSectionType.Text,
    filterKey: "serial",
    label: "Serial",
  },
  {
    type: FilterSectionType.Text,
    filterKey: "dnsPrefix",
    label: "DNS prefix",
  },
  {
    type: FilterSectionType.Text,
    filterKey: "zuoraSubscriptionKey",
    label: "Zuora subscription number",
  },
];

const complimentaryTypeFilterSection: FilterSectionFieldProps = {
  type: FilterSectionType.Select,
  filterKey: "complimentaryType",
  label: "Complimentary type",
  deselectOptionLabel: "All complimentary types",
  selectItems: Object.values(ComplimentaryType).map((type) => ({
    value: type,
    label: type,
  })),
};

const rateCardCodeFilterSection: FilterSectionFieldProps = {
  type: FilterSectionType.Text,
  filterKey: "rateCardCode",
  label: "Rate card code",
};

// Extended filters - displayed in collapsible sections.
// Pass the current lifecycleType filter value so the complimentary type sub-filter
// is only shown when it is relevant.
export function subscriptionFilterSections(
  lifecycleType: FilterValue,
  rateCardCodeFilterEnabled: boolean
): FilterSectionFieldProps[] {
  return [
    {
      type: FilterSectionType.Select,
      filterKey: "status",
      label: "Status",
      deselectOptionLabel: "All statuses",
      selectItems: [
        { value: "Pending", label: "Pending" },
        { value: "Active", label: "Active" },
        { value: "Expired", label: "Expired" },
        { value: "Canceled", label: "Canceled" },
      ],
    },
    {
      type: FilterSectionType.Select,
      filterKey: "tier",
      label: "Tier",
      deselectOptionLabel: "All tiers",
      selectItems: Object.values(Tier).map((tier) => ({
        value: tier,
        label: tier,
      })),
    },
    {
      type: FilterSectionType.Select,
      filterKey: "lifecycleType",
      label: "Lifecycle type",
      deselectOptionLabel: "All types",
      selectItems: [
        { value: "Trial", label: "Trial" },
        { value: "Paid", label: "Paid" },
        { value: "Evergreen", label: "Evergreen" },
        { value: "Complimentary", label: "Complimentary" },
      ],
    },
    ...(lifecycleType === "Complimentary" ? [complimentaryTypeFilterSection] : []),
    {
      type: FilterSectionType.Text,
      filterKey: "friendlyName",
      label: "Friendly name",
    },
    {
      type: FilterSectionType.Text,
      filterKey: "ownerEmail",
      label: "Owner email",
    },
    ...(rateCardCodeFilterEnabled ? [rateCardCodeFilterSection] : []),
    {
      type: FilterSectionType.Date,
      filterKey: "termStartAfter",
      label: "Term start after",
    },
    {
      type: FilterSectionType.Date,
      filterKey: "termStartBefore",
      label: "Term start before",
    },
    {
      type: FilterSectionType.Date,
      filterKey: "termEndAfter",
      label: "Term end after",
    },
    {
      type: FilterSectionType.Date,
      filterKey: "termEndBefore",
      label: "Term end before",
    },
  ];
}

// Filter mapping for API integration
export const subscriptionFilterKeys = [
  ...subscriptionHeaderSections,
  ...subscriptionFilterSections("Complimentary", true),
].map((section) => section.filterKey);

// Default filter values
export const subscriptionDefaultFilterValue = {};

// Default column visibility for the subscription list
export const subscriptionDefaultColumnVisibility = {
  createdByUser: false,
  expiryDate: false,
};
